import type { ApiError, ApiErrorCode, References } from "@delta/shared";

export class ApiFailure extends Error {
  constructor(readonly status: number, readonly code: ApiErrorCode, message: string, readonly missing: string[] = [], readonly fields: Record<string, string> = {}, readonly limit?: number, readonly references: References = {}, readonly body: Record<string, unknown> = {}) {
    super(message);
  }
}

let onUnauthenticated: () => void = () => undefined;
export const setUnauthenticatedHandler = (fn: () => void) => { onUnauthenticated = fn; };
/** A demo that has ended (migration 055) signs out like any ended session; the sign-in page then says why. */
export const DEMO_NOTE = "delta.demoNote";
const signedOut = (code: string) => {
  if (code === "demo_ended") { try { sessionStorage.setItem(DEMO_NOTE, "ended"); } catch { /* storage blocked */ } }
  onUnauthenticated();
};

/** A request a sale below cost was refused on, for the approval dialog (components/BelowCost.tsx). */
export interface ApiRequest { method: "GET" | "POST" | "PUT" | "DELETE"; url: string; body?: unknown }
/** Shows the approval dialog; resolves with the approval to send again with, or null when the user gives up. */
type BelowCostHandler = (e: ApiFailure, req: ApiRequest) => Promise<string | null>;
let onBelowCost: BelowCostHandler | null = null;
export const setBelowCostHandler = (fn: BelowCostHandler | null) => { onBelowCost = fn; };
const BELOW_COST = /^below_cost_approval_/;

/**
 * JSON call to the Delta API. The session cookie travels automatically (same origin, HTTP-only); every call
 * carries the anti-forgery header the server requires on state-changing requests.
 */
export async function api<T>(method: "GET" | "POST" | "PUT" | "DELETE", url: string, body?: unknown, approval?: string): Promise<T> {
  const headers: Record<string, string> = { "x-requested-with": "delta", accept: "application/json" };
  if (body !== undefined) headers["content-type"] = "application/json";
  // A sale below cost is sent again with the approval the dialog obtained (migration 039).
  if (approval) headers["x-below-cost-approval"] = approval;
  const r = await fetch("/api" + url, { method, headers, credentials: "same-origin", body: body === undefined ? undefined : JSON.stringify(body) });
  if (r.status === 204) return undefined as T;
  const data = await r.json().catch(() => null);
  if (!r.ok) {
    const e = (data as ApiError | null)?.error;
    if (r.status === 401 && (e?.code === "unauthenticated" || e?.code === "demo_ended")) signedOut(e.code);
    const failure = new ApiFailure(r.status, e?.code ?? "internal", e?.message ?? r.statusText, e?.missing, e?.fields, e?.limit, e?.references, (e ?? {}) as Record<string, unknown>);
    if (r.status === 409 && BELOW_COST.test(failure.code) && onBelowCost) {
      const id = await onBelowCost(failure, { method, url, body });
      if (id) return api<T>(method, url, body, id);
    }
    throw failure;
  }
  return data as T;
}

/** Uploads a file as the raw request body with its own Content-Type (the logo endpoint checks type and size). */
export async function apiUpload<T>(url: string, file: Blob): Promise<T> {
  const r = await fetch("/api" + url, { method: "POST", headers: { "x-requested-with": "delta", accept: "application/json", "content-type": file.type }, credentials: "same-origin", body: file });
  const data = await r.json().catch(() => null);
  if (!r.ok) {
    const e = (data as ApiError | null)?.error;
    if (r.status === 401 && (e?.code === "unauthenticated" || e?.code === "demo_ended")) signedOut(e.code);
    throw new ApiFailure(r.status, e?.code ?? "internal", e?.message ?? r.statusText, e?.missing, e?.fields, e?.limit, e?.references);
  }
  return data as T;
}
